import React, { useState } from "react";
import InputMask from "react-input-mask";
import CreateLicense from "../../components/CreateLicense/CreateLicense";
import SupplyChainPartnersEditor from "../../components/SupplyChainPartners/SupplyChainPartnersEditor";
import {
    OPERATION_TYPE_PROFILES,
    buildPartnersPayload,
    emptyPartnerRow,
    getListRules,
    validatePartners,
} from "../../constants/supplyChain";
import "./registrationPage.css";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";

const RegistrationPage = () => {
    const toast = useToast();
    const [showSuccess, setShowSuccess] = useState(false);
    const [company, setCompany] = useState([]);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [title, setTitle] = useState("owner");
    const [businessType, setBusinessType] = useState([]); 
    const [state, setState] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [address, setAddress] = useState([]);
    const [postCode, setPostCode] = useState([]);
    const [city, setCity] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [files, setFiles] = useState([]);
    const [complianceDocs, setComplianceDocs] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [usdaLicenseNumber, setUsdaLicenseNumber] = useState("");
    const [ein, setEin] = useState("");
    // Supply chain: vendors (upstream) + customers (downstream). Kept when the business type
    // changes — both lists mean the same for every type; lists a type doesn't have (retailer
    // customers) are hidden and never submitted.
    const [partnerRows, setPartnerRows] = useState([]);
    const [operationType, setOperationType] = useState("");
    // Honeypot: hidden from people, filled by bots. The server silently drops such submissions.
    const [faxNumber, setFaxNumber] = useState("");

    const hasSupplyChainData =
        !!operationType || partnerRows.some((r) => r.linkedLicenseId || r.partnerName.trim());

    const hasFormData = !!(firstName || lastName || email || phoneNumber || state || hasSupplyChainData);
    useUnsavedChanges(hasFormData, { message: "You have registration data entered. Are you sure you want to leave?" });

    const handlePhoneNumberChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, "");
        setPhoneNumber(numericValue);
    };

    const addIndustry = (index, industry) => {
        const updated = [...industries];
        updated[index] = industry;
        setIndustries(updated);
    };

    const addCompanyName = (index, companyName) => {
        const updated = [...company];
        updated[index] = companyName;
        setCompany(updated);
    };

    const addStreetAddress = (index, streetAddress) => {
        const updated = [...address];
        updated[index] = streetAddress;
        setAddress(updated);
    };

    const addCity = (index, cityVal) => {
        const updated = [...city];
        updated[index] = cityVal;
        setCity(updated);
    };

    const addState = (index, stateVal) => {
        const updated = [...state];
        updated[index] = stateVal;
        setState(updated);
    };

    const getZip = (index, zip) => {
        const updated = [...postCode];
        updated[index] = zip;
        setPostCode(updated);
    };

    const addBusinessType = (index, type) => {
        const updated = [...businessType];
        const changed = updated[index] !== type;
        updated[index] = type;
        setBusinessType(updated);

        // Switching type mid-form: make sure every list the new type requires has a row to
        // type into, drop the grower-only operation type, and clear supply-chain errors.
        if (changed && index === 0) {
            const rules = getListRules(type);
            setPartnerRows((prev) => {
                const next = [...prev];
                for (const direction of ["upstream", "downstream"]) {
                    if (rules[direction] > 0 && !next.some((r) => r.direction === direction)) next.push(emptyPartnerRow(direction));
                }
                return next;
            });
            if (!OPERATION_TYPE_PROFILES.includes(type)) setOperationType("");
            setValidationErrors((prev) => {
                const next = {};
                for (const [k, v] of Object.entries(prev)) {
                    if (!k.startsWith("sc_")) next[k] = v;
                }
                return next;
            });
        }
    };

    const handleClick = (event) => {
        setIsButtonDisabled(true);
        handleSubmit(event);
        setTimeout(() => {
            setIsButtonDisabled(false);
        }, 3000);
    };

    const complianceCheck = () => {
        const recAdultUseStates = [
            "AK","AZ","CA","CO","CT","DE","DC","IL","ME","MD","MA","MI","MN",
            "MO","MT","NV","NJ","NM","NY","OH","OR","RI","VT","VA","WA",
        ];
        const medicalStates = [
            "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","HI","IL","LA",
            "ME","MD","MA","MI","MN","MS","MO","MT","NV","NH","NJ","NM","NY",
            "ND","OH","OK","OR","PA","RI","SD","UT","VT","VA","WA","WV",
        ];

        const s = state[0];
        const ind = industries[0];
        if (ind && s) {
            if (ind.toLowerCase() === "rec / adult use" && !recAdultUseStates.includes(s)) return 1;
            if (ind.toLowerCase() === "medical" && !medicalStates.includes(s)) return 1;
        }
        return true;
    };

    const validateRegistration = () => {
        const errs = {};
        if (!firstName.trim()) errs.firstName = "First name is required.";
        if (!lastName.trim()) errs.lastName = "Last name is required.";
        if (!email.trim()) errs.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address.";
        if (!phoneNumber || phoneNumber.replace(/\D/g, "").length < 10) errs.phoneNumber = "Enter a valid 10-digit phone number.";
        if (!termsAccepted) errs.terms = "You must agree to the terms and conditions.";

        if (!company[0]?.trim()) errs["company_0"] = "Company name is required.";
        if (!industries[0]) errs["industry_0"] = "Industry is required.";
        if (!address[0]?.trim()) errs["address_0"] = "Street address is required.";
        if (!city[0]?.trim()) errs["city_0"] = "City is required.";
        if (!state[0]) errs["state_0"] = "State is required.";
        if (!postCode[0]?.trim()) errs["zip_0"] = "Zip code is required.";
        else if (!/^\d{5}(-\d{4})?$/.test(postCode[0].trim())) errs["zip_0"] = "Enter a valid zip code (e.g. 90210).";
        if (!businessType[0]) errs["businessType_0"] = "Business type is required.";

        if (!ein.trim()) errs.ein = "EIN is required.";

        const isRetailer = businessType[0] === "retailer";
        if (!isRetailer && !usdaLicenseNumber.trim()) {
            errs.usdaLicenseNumber = "USDA license number is required for this business type.";
        }

        if (businessType[0]) {
            for (const [k, v] of Object.entries(validatePartners(businessType[0], partnerRows, operationType))) {
                errs[`sc_${k}`] = v;
            }
        }

        setValidationErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const clearValidationError = (key) => {
        setValidationErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    };

    const COMPLIANCE_DOC_TYPES = ["Business License", "Certificate of Insurance", "Tax Certificate", "Other"];
    const MAX_COMPLIANCE_DOCS = 5;
    const MAX_DOC_SIZE = 15 * 1024 * 1024;
    const ACCEPTED_DOC_TYPES = ["application/pdf", "image/jpeg", "image/png"];

    const addComplianceDoc = () => {
        if (complianceDocs.length >= MAX_COMPLIANCE_DOCS) return;
        setComplianceDocs([...complianceDocs, { file: null, documentType: COMPLIANCE_DOC_TYPES[0], customType: "" }]);
    };

    const removeComplianceDoc = (index) => {
        setComplianceDocs(complianceDocs.filter((_, i) => i !== index));
    };

    const updateComplianceDoc = (index, field, value) => {
        const updated = [...complianceDocs];
        updated[index] = { ...updated[index], [field]: value };
        setComplianceDocs(updated);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateRegistration()) {
            toast.error("Please fix the highlighted fields before submitting.");
            return;
        }

        const check = complianceCheck();
        if (check !== true) {
            toast.error("Your license defines an industry that is not compliant with that state.");
            return;
        }

        const formData = new FormData();
        formData.append("firstName", firstName);
        formData.append("lastName", lastName);
        formData.append("email", email);
        formData.append("phoneNumber", phoneNumber);
        formData.append("title", title);
        formData.append("industries", industries);
        formData.append("company", company);
        formData.append("address", address);
        formData.append("city", city);
        formData.append("state", state);
        formData.append("postCode", postCode);
        formData.append("businessType", businessType);
        formData.append("usdaLicenseNumber", usdaLicenseNumber);
        formData.append("ein", ein);
        // Only the lists the selected business type has are submitted.
        formData.append("partners", JSON.stringify(buildPartnersPayload(businessType[0], partnerRows)));
        if (OPERATION_TYPE_PROFILES.includes(businessType[0])) formData.append("operationType", operationType);
        formData.append("faxNumber", faxNumber);

        if (files.length > 0) {
            files.forEach((file) => formData.append("files", file));
        }

        const validDocs = complianceDocs.filter((d) => d.file);
        if (validDocs.length > 0) {
            const docTypes = validDocs.map((d) =>
                d.documentType === "Other" ? d.customType.trim() : d.documentType
            );
            validDocs.forEach((d) => formData.append("complianceDocs", d.file));
            formData.append("complianceDocTypes", JSON.stringify(docTypes));
        }

        apiClient
            .post("/register/newRegisteredUser", formData)
            .then(() => setShowSuccess(true))
            .catch((error) => {
                if (error.response?.status === 429) {
                    toast.error("Too many registration attempts from this network. Please try again in an hour.");
                    return;
                }
                const msg = error.response?.data?.message || error.response?.data || error.message;
                console.error("Registration error:", msg);
                toast.error(`Registration failed: ${msg}`);
            });
    };

    return (
        <div className="registration-page fade-down">
            <form onSubmit={handleSubmit} className="registration-form">
                {/* Branded header */}
                <div className="reg-header">
                    <img src="/DYC-logo-ColorWheel-4color.svg" alt="DYC Logo" className="dyc_logo" />
                    <h1>Create Account</h1>
                </div>

                {/* Form body */}
                <div className="reg-body">
                    <p className="reg-required-note">Required fields are marked with an asterisk (<span className="reg-required-star">*</span>).</p>
                    {/* Section: Account Details */}
                    <div className="reg-section-label"><span>Account Details</span></div>
                    <div className="reg-fields">
                        <div className="form-row">
                            <div className="reg-field">
                                <label className="reg-required">First Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="First name"
                                    value={firstName}
                                    onChange={(e) => { setFirstName(e.target.value); clearValidationError("firstName"); }}
                                    className={`registration-input${validationErrors.firstName ? " input-error" : ""}`}
                                />
                                {validationErrors.firstName && <span className="field-error">{validationErrors.firstName}</span>}
                            </div>
                            <div className="reg-field">
                                <label className="reg-required">Last Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Last name"
                                    value={lastName}
                                    onChange={(e) => { setLastName(e.target.value); clearValidationError("lastName"); }}
                                    className={`registration-input${validationErrors.lastName ? " input-error" : ""}`}
                                />
                                {validationErrors.lastName && <span className="field-error">{validationErrors.lastName}</span>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="reg-field">
                                <label className="reg-required">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); clearValidationError("email"); }}
                                    className={`registration-input${validationErrors.email ? " input-error" : ""}`}
                                />
                                {validationErrors.email && <span className="field-error">{validationErrors.email}</span>}
                            </div>
                            <div className="reg-field">
                                <label className="reg-required">Phone Number</label>
                                <InputMask
                                    mask="(999) 999-9999"
                                    value={phoneNumber}
                                    onChange={(e) => { handlePhoneNumberChange(e); clearValidationError("phoneNumber"); }}
                                    placeholder="(555) 555-5555"
                                >
                                    {(inputProps) => (
                                        <input
                                            {...inputProps}
                                            type="tel"
                                            required
                                            className={`registration-input${validationErrors.phoneNumber ? " input-error" : ""}`}
                                        />
                                    )}
                                </InputMask>
                                {validationErrors.phoneNumber && <span className="field-error">{validationErrors.phoneNumber}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Section: Business Details */}
                    <div className="reg-section-label"><span>Business Details</span></div>
                    <CreateLicense
                        index={0}
                        addIndustry={addIndustry}
                        industry={industries[0]}
                        addStreetAddress={addStreetAddress}
                        streetAddress={address[0]}
                        addCity={addCity}
                        city={city[0]}
                        addState={addState}
                        zip={postCode[0]}
                        state={state[0]}
                        addBusinessType={addBusinessType}
                        type={businessType[0]}
                        getZip={getZip}
                        addCompanyName={addCompanyName}
                        companyName={company[0]}
                        validationErrors={validationErrors}
                        clearValidationError={clearValidationError}
                    />
                    <div className="reg-fields">
                        <div className="form-row">
                            <div className="reg-field">
                                <label className="reg-required">EIN</label>
                                <input
                                    type="text"
                                    placeholder="12-3456789"
                                    aria-required="true"
                                    value={ein}
                                    onChange={(e) => { setEin(e.target.value); clearValidationError("ein"); }}
                                    className={`registration-input${validationErrors.ein ? " input-error" : ""}`}
                                />
                                {validationErrors.ein && <span className="field-error">{validationErrors.ein}</span>}
                            </div>
                            <div className="reg-field">
                                <label className={businessType[0] === "retailer" ? undefined : "reg-required"}>USDA License Number{businessType[0] === "retailer" ? " (optional for retailers)" : ""}</label>
                                <input
                                    type="text"
                                    placeholder={businessType[0] === "retailer" ? "Optional" : "e.g. USDA-12345"}
                                    aria-required={businessType[0] === "retailer" ? undefined : "true"}
                                    value={usdaLicenseNumber}
                                    onChange={(e) => { setUsdaLicenseNumber(e.target.value); clearValidationError("usdaLicenseNumber"); }}
                                    className={`registration-input${validationErrors.usdaLicenseNumber ? " input-error" : ""}`}
                                />
                                {validationErrors.usdaLicenseNumber && <span className="field-error">{validationErrors.usdaLicenseNumber}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Section: Supply chain — who this business buys from (vendors) and sells to
                        (customers). Lists and minimums depend on the business type
                        (constants/supplyChain.js). */}
                    {businessType[0] && (
                        <>
                            <div className="reg-section-label"><span>Supply Chain</span></div>
                            <div className="reg-fields">
                                <SupplyChainPartnersEditor
                                    idPrefix="reg-sc"
                                    profile={businessType[0]}
                                    rows={partnerRows}
                                    onChange={(next) => {
                                        setPartnerRows(next);
                                        setValidationErrors((prev) => {
                                            const cleaned = {};
                                            for (const [k, v] of Object.entries(prev)) if (!k.startsWith("sc_") || k === "sc_operationType") cleaned[k] = v;
                                            return cleaned;
                                        });
                                    }}
                                    errors={Object.fromEntries(
                                        Object.entries(validationErrors).filter(([k]) => k.startsWith("sc_")).map(([k, v]) => [k.slice(3), v])
                                    )}
                                    searchMode="public"
                                    operationType={operationType}
                                    onOperationTypeChange={(v) => {
                                        setOperationType(v);
                                        clearValidationError("sc_operationType");
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {/* Honeypot — hidden from people and assistive tech; bots fill it. */}
                    <div className="reg-hp" aria-hidden="true">
                        <label htmlFor="reg-fax">Fax number</label>
                        <input
                            id="reg-fax"
                            name="faxNumber"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={faxNumber}
                            onChange={(e) => setFaxNumber(e.target.value)}
                        />
                    </div>

                    {/* Section: Compliance Documents (Optional) */}
                    <div className="reg-section-label"><span>Compliance Documents</span></div>
                    <div className="reg-fields">
                        <p className="reg-section-hint">
                            Upload up to {MAX_COMPLIANCE_DOCS} compliance documents (PDF, JPG, PNG — max 15 MB each).
                        </p>
                        {complianceDocs.map((doc, idx) => (
                            <div key={idx} className="compliance-doc-row">
                                <div className="reg-field" style={{ flex: 1 }}>
                                    <label>Document Type</label>
                                    <select
                                        className="registration-input"
                                        value={doc.documentType}
                                        onChange={(e) => updateComplianceDoc(idx, "documentType", e.target.value)}
                                    >
                                        {COMPLIANCE_DOC_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                {doc.documentType === "Other" && (
                                    <div className="reg-field" style={{ flex: 1 }}>
                                        <label>Custom Type</label>
                                        <input
                                            type="text"
                                            className="registration-input"
                                            placeholder="Enter document type"
                                            maxLength={100}
                                            value={doc.customType}
                                            onChange={(e) => updateComplianceDoc(idx, "customType", e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="reg-field" style={{ flex: 1 }}>
                                    <label>File</label>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="registration-input"
                                        onChange={(e) => {
                                            const f = e.target.files[0];
                                            if (f && f.size > MAX_DOC_SIZE) {
                                                toast.error("File exceeds 15 MB limit.");
                                                e.target.value = "";
                                                return;
                                            }
                                            if (f && !ACCEPTED_DOC_TYPES.includes(f.type)) {
                                                toast.error("Only PDF, JPG, and PNG files are allowed.");
                                                e.target.value = "";
                                                return;
                                            }
                                            updateComplianceDoc(idx, "file", f || null);
                                        }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="compliance-doc-remove"
                                    onClick={() => removeComplianceDoc(idx)}
                                    title="Remove document"
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                        {complianceDocs.length < MAX_COMPLIANCE_DOCS && (
                            <button type="button" className="compliance-doc-add" onClick={addComplianceDoc}>
                                + Add Document
                            </button>
                        )}
                    </div>

                    {/* Footer: Terms + Register */}
                    <div className="reg-footer">
                        <div className="terms-of-service">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={termsAccepted}
                                onChange={(e) => { setTermsAccepted(e.target.checked); clearValidationError("terms"); }}
                            />
                            <div className="terms-text reg-required">
                                I accept the{" "}
                                <a
                                    className="termsOfService"
                                    href="https://dycpublicdocumentsandagreements2.s3.us-east-2.amazonaws.com/Terms_of_Service_5-7-25.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Terms of Service
                                </a>
                            </div>
                        </div>
                        {validationErrors.terms && <span className="field-error">{validationErrors.terms}</span>}
                        <button type="submit" className="register-button" onClick={handleClick} disabled={isButtonDisabled}>
                            {isButtonDisabled ? "Submitting…" : "Create Account"}
                        </button>
                        <div className="login-link">
                            Already have an account?{" "}
                            <a href="/">Sign in</a>
                        </div>
                    </div>
                </div>
            </form>

            {showSuccess && (
                <div className="reg-success-overlay">
                    <div className="reg-success-modal">
                        <div className="reg-success-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M8 12l3 3 5-5" />
                            </svg>
                        </div>
                        <h2 className="reg-success-title">Registration Submitted</h2>
                        <p className="reg-success-message">
                            An administrator has been notified and will verify your account shortly.
                            Once approved, you'll receive an email with login instructions.
                        </p>
                        <a href="/" className="reg-success-button">
                            Back to Sign In
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegistrationPage;
 